import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { IconButton, Stack } from '@mui/material';
import { DynamicTooltipChip } from 'src/components/dynamicTooltipChip/DynamicTooltipChip';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { RowText } from 'src/features/assemblies/listAssemblyPage/modules/assemblyTableRow/AssembyTableRow.styled';
import { isDy } from 'src/features/components/editComponentPage/EditComponentPage.helper';
import {
  IComponent,
  IExpiredStatus,
  IStatus,
  IStatusLabel,
} from 'src/interfaces';
import {
  getComponentDisplayName,
  lang,
  showSoftSellComponent,
} from 'src/utils';

import { ComponentMenu } from '../componentMenu/ComponentMenu';
import { ComponentTableCellContentProps } from './ComponentTableRow.interface';
import { ChipContainer, RowContainer } from './ComponentTableRow.styled';

export const ComponentTableCellContent = ({
  column,
  isExpiringSoon,
  props,
  onMenuClick,
  menu,
  deleteComponentModal,
  duplicateComponentModal,
  convertToDraftModal,
}: ComponentTableCellContentProps): JSX.Element | null => {
  const columnValues = props.row[column.key as keyof IComponent];

  switch (column.key) {
    case 'name':
      return typeof columnValues === 'string' ? (
        <CustomTooltip
          width={300}
          placement="bottom"
          title={columnValues}
          overflowWidth={180}>
          <Stack direction="row" spacing={1} alignItems="center">
            <span>{columnValues}</span>
          </Stack>
        </CustomTooltip>
      ) : null;

    case 'status':
      if (columnValues === IStatus.DRAFT) {
        return (
          <ChipContainer>
            <DynamicTooltipChip
              chipName={IStatusLabel[props.row.status]}
              color="primary"
              variant="outlined"
              size="small"
            />
            {!!props?.row?.usable_as_override && (
              <DynamicTooltipChip
                chipName={lang.ownerOperatorOverridePill}
                color="success"
                size="small"
              />
            )}
            {showSoftSellComponent(props?.row) && (
              <DynamicTooltipChip
                chipName={lang.softSell}
                color="info"
                variant="outlined"
                size="small"
              />
            )}
            {isDy(props?.row?.config?.productsSource) && (
              <DynamicTooltipChip
                chipName={lang.dy}
                color="primary"
                size="small"
              />
            )}
          </ChipContainer>
        );
      }
      if (columnValues === IStatus.READY) {
        return (
          <ChipContainer>
            {isExpiringSoon !== IExpiredStatus.expired && (
              <DynamicTooltipChip
                chipName={IStatusLabel[props.row.status]}
                color="success"
                variant="outlined"
                size="small"
              />
            )}
            {isExpiringSoon && (
              <DynamicTooltipChip
                chipName={isExpiringSoon}
                color="error"
                variant="outlined"
                size="small"
              />
            )}
            {!!props?.row?.usable_as_override && (
              <DynamicTooltipChip
                chipName={lang.ownerOperatorOverridePill}
                color="success"
                size="small"
              />
            )}
            {showSoftSellComponent(props?.row) && (
              <DynamicTooltipChip
                chipName={lang.softSell}
                color="info"
                variant="outlined"
                size="small"
              />
            )}
            {isDy(props?.row?.config?.productsSource) && (
              <DynamicTooltipChip
                chipName={lang.dy}
                color="primary"
                size="small"
              />
            )}
          </ChipContainer>
        );
      }

      return null;

    case 'type':
      return (
        <RowContainer>
          <RowText>
            {getComponentDisplayName(props.row.component_type?.name)}
          </RowText>
        </RowContainer>
      );

    case 'size':
      return (
        <RowContainer>
          <RowText>{`${props.row.width} x ${props.row.height}`}</RowText>
        </RowContainer>
      );

    case 'menu':
      return (
        <>
          <IconButton
            data-testid="row-menu-icon"
            onClick={onMenuClick}
            sx={{ visibility: 'visible' }}>
            <MoreVertIcon />
          </IconButton>
          <ComponentMenu
            anchorEl={menu.anchorEl}
            onClose={menu.onCloseMenu}
            card={props.row}
            onDeleteClick={deleteComponentModal.onOpenModal}
            onDuplicateClick={duplicateComponentModal.onOpenModal}
            onConvertDraftClick={convertToDraftModal.onOpenModal}
          />
        </>
      );

    default:
      return typeof columnValues === 'string' ? (
        <span>{columnValues}</span>
      ) : null;
  }
};
